<template>
  <div>
    <v-tabs right v-model="tab" @change="onTabChange">
      <v-tab key="itemlevel-tab">{{ $t('information.tabs.item_level') }}</v-tab>
      <v-tab key="mplus-tab">{{ $t('information.tabs.mplus') }}</v-tab>
      <v-tab key="guild-bank-tab">{{ $t('information.tabs.guildbank') }}</v-tab>
      <v-tab key="auction-search-tab">{{ $t('information.tabs.auction_search') }}</v-tab>
      <v-tab key="darkmoon-fairy-tab">{{ $t('information.tabs.darkmoon_faire') }}</v-tab>
      <v-tab key="race-class-tab">{{ $t('information.tabs.race_class') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="mt-3">
      <!-- Item level -->
      <v-tab-item>
        <v-row class="pa-2">
          <v-col>
            <v-autocomplete :items="seasonsItemLevel" item-text="name" item-value="id" v-model="currentSeasonItemLevel" label="Season"/>
          </v-col>
        </v-row>

        <!-- Top: Dungeons + Bountiful Delves -->
        <v-row class="pa-2">
          <v-col cols="12" md="auto">
            <h3 class="mb-2">{{ $t("item_level.dungeons") }}</h3>
            <v-simple-table dark dense class="itemlevel-table">
              <template v-slot:default>
                <thead>
                <tr class="table-header-row">
                  <th class="col-num">Level</th>
                  <th class="col-num">{{ $t("item_level.instance") }}</th>
                  <th class="col-num">{{ $t("item_level.vault") }}</th>
                  <th>{{ $t("item_level.crest_rewards") }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in currentSeasonDefItemLevel.dungeonTable" :key="`d_${row.level}`">
                  <td class="col-num" style="background: rgba(255,255,255,0.04)">{{ row.level }}</td>
                  <td class="col-num" :class="ilvlTrackClass(row.loot)">{{ row.loot ?? '-' }}</td>
                  <td class="col-num" :class="ilvlTrackClass(row.vault)">
                    <span v-if="row.vault != null">{{ row.vault }}</span>
                    <span v-else class="grey--text">n/a</span>
                  </td>
                  <td class="crest-cell-left"
                      :class="[
                        row.crestReward && row.crestReward.length === 1 ? crestCellClass(row.crestReward[0].crest) : '',
                        row.crestReward && row.crestReward.length > 1 ? 'crest-multi' : ''
                      ]">
                    <template v-if="row.crestReward && row.crestReward.length > 1">
                      <span v-for="(cr, ci) in row.crestReward" :key="`dc_${ci}`"
                            class="crest-half" :class="'cell--crest-' + cr.crest">
                        <template v-if="cr.amount">{{ cr.amount }}× </template>{{ crestLabel(cr.crest) }}
                      </span>
                    </template>
                    <template v-else-if="row.crestReward && row.crestReward.length === 1">
                      <span class="crest-inline" :class="'text--crest-' + row.crestReward[0].crest">
                        <template v-if="row.crestReward[0].amount">{{ row.crestReward[0].amount }}× </template>{{ crestLabel(row.crestReward[0].crest) }}
                      </span>
                    </template>
                    <template v-else><span class="grey--text">n/a</span></template>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

          <v-col cols="12" md="auto">
            <h3 class="mb-2">{{ $t("item_level.bountiful_delves") }}</h3>
            <v-simple-table dark dense class="itemlevel-table">
              <template v-slot:default>
                <thead>
                <tr class="table-header-row">
                  <th class="col-num">{{ $t("item_level.tier") }}</th>
                  <th class="col-num">{{ $t("item_level.instance") }}</th>
                  <th class="col-num">{{ $t("item_level.map") }}</th>
                  <th class="col-num">{{ $t("item_level.vault") }}</th>
                  <th>{{ $t("item_level.crests_instance") }}</th>
                  <th>{{ $t("item_level.crests_nemesis") }} *</th>
                  <th>{{ $t("item_level.crests_stash") }} **</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in currentSeasonDefItemLevel.delveTable" :key="`b_${row.tier}`">
                  <td class="col-num" style="background: rgba(255,255,255,0.04)">{{ row.tier }}</td>
                  <td class="col-num" :class="ilvlTrackClass(row.loot)">{{ row.loot ?? '-' }}</td>
                  <td class="col-num" :class="ilvlTrackClass(row.map)">
                    <span v-if="row.map != null">{{ row.map }}</span>
                    <span v-else-if="row.tier != null && row.tier < 4" class="grey--text">n/a</span>
                    <span v-else>-</span>
                  </td>
                  <td class="col-num" :class="ilvlTrackClass(row.vault)">{{ row.vault ?? '-' }}</td>
                  <td class="crest-cell-left"
                      :class="[
                        row.lootCrests && row.lootCrests.length === 1 ? crestCellClass(row.lootCrests[0].crest) : '',
                        row.lootCrests && row.lootCrests.length > 1 ? 'crest-multi' : ''
                      ]">
                    <template v-if="row.lootCrests && row.lootCrests.length > 1">
                      <span v-for="(cr, ci) in row.lootCrests" :key="`lc_${ci}`"
                            class="crest-half" :class="'cell--crest-' + cr.crest">
                        <template v-if="cr.amount">{{ cr.amount }}× </template>{{ crestLabel(cr.crest) }}
                      </span>
                    </template>
                    <template v-else-if="row.lootCrests && row.lootCrests.length === 1">
                      <span class="crest-inline" :class="'text--crest-' + row.lootCrests[0].crest">
                        <template v-if="row.lootCrests[0].amount">{{ row.lootCrests[0].amount }}× </template>{{ crestLabel(row.lootCrests[0].crest) }}
                      </span>
                    </template>
                    <template v-else><span class="grey--text">n/a</span></template>
                  </td>
                  <td class="crest-cell-left"
                      :class="[
                        row.nemesisCrests && row.nemesisCrests.length === 1 ? crestCellClass(row.nemesisCrests[0].crest) : '',
                        row.nemesisCrests && row.nemesisCrests.length > 1 ? 'crest-multi' : ''
                      ]">
                    <template v-if="row.nemesisCrests && row.nemesisCrests.length > 1">
                      <span v-for="(cr, ci) in row.nemesisCrests" :key="`nc_${ci}`"
                            class="crest-half" :class="'cell--crest-' + cr.crest">
                        <template v-if="cr.amount">{{ cr.amount }}× </template>{{ crestLabel(cr.crest) }}
                      </span>
                    </template>
                    <template v-else-if="row.nemesisCrests && row.nemesisCrests.length === 1">
                      <span class="crest-inline" :class="'text--crest-' + row.nemesisCrests[0].crest">
                        <template v-if="row.nemesisCrests[0].amount">{{ row.nemesisCrests[0].amount }}× </template>{{ crestLabel(row.nemesisCrests[0].crest) }}
                      </span>
                    </template>
                    <template v-else><span class="grey--text">n/a</span></template>
                  </td>
                  <td class="crest-cell-left"
                      :class="[
                        row.stashCrests && row.stashCrests.length === 1 ? crestCellClass(row.stashCrests[0].crest) : '',
                        row.stashCrests && row.stashCrests.length > 1 ? 'crest-multi' : ''
                      ]">
                    <template v-if="row.stashCrests && row.stashCrests.length > 1">
                      <span v-for="(cr, ci) in row.stashCrests" :key="`sc_${ci}`"
                            class="crest-half" :class="'cell--crest-' + cr.crest">
                        <template v-if="cr.amount">{{ cr.amount }}× </template>{{ crestLabel(cr.crest) }}
                      </span>
                    </template>
                    <template v-else-if="row.stashCrests && row.stashCrests.length === 1">
                      <span class="crest-inline" :class="'text--crest-' + row.stashCrests[0].crest">
                        <template v-if="row.stashCrests[0].amount">{{ row.stashCrests[0].amount }}× </template>{{ crestLabel(row.stashCrests[0].crest) }}
                      </span>
                    </template>
                    <template v-else><span class="grey--text">n/a</span></template>
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="caption grey--text mt-1">
              * {{ $t("item_level.delve_nemesis_hint") }}
            </div>
            <div class="caption grey--text mt-1">
              ** {{ $t("item_level.delve_stash_hint") }}
            </div>
          </v-col>
        </v-row>

        <!-- Bottom: Detail Table with tracks, crests und crafting -->
        <v-row class="pa-2">
          <v-col cols="12">
            <h3 class="mb-2">{{ $t("item_level.season_details") }}</h3>
            <div class="combined-table-wrap">
              <v-simple-table dark dense class="combined-table">
                <template v-slot:default>
                  <thead>
                  <!-- Group header -->
                  <tr>
                    <th class="col-ilvl">ilvl</th>
                    <th colspan="10" class="group-header group-header--tracks">{{ $t("item_level.upgrade_tracks") }}</th>
                    <th colspan="2" class="group-header group-header--crests">{{ $t("item_level.crests") }}</th>
                    <th colspan="2" class="group-header group-header--crafting">{{ $t("item_level.crafting") }}</th>
                    <th v-if="hasRaidTable" class="group-header group-header--raid col-raid-header">{{ $t("item_level.raid") }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in combinedRows" :key="`row_${row.ilvl}`">
                    <td class="col-ilvl">{{ row.ilvl }}</td>

                    <!-- Upgrade Tracks (2 cols each: track name + rank number) -->
                    <template v-for="t in trackKeys">
                      <td :key="`c_tname_${row.ilvl}_${t}`"
                          :class="[row.tracks[t] ? trackCellClass(t) : '', row.tracks[t] ? 'track-name-cell' : '', row.tracks[t] && row.tracks[t].isFirst ? 'track-name-first' : '', row.tracks[t] && row.tracks[t].isLast ? 'track-name-last' : '', row.tracks[t] && row.tracks[t].isSingle ? 'track-name-single' : '']"
                          class="text-center cell-data col-track-name">
                        <span v-if="row.tracks[t] && row.tracks[t].isFirst" class="cell-label track-name-label">{{ $t(`item_level.track.${t}`) }}</span>
                      </td>
                      <td :key="`c_trank_${row.ilvl}_${t}`"
                          :class="row.tracks[t] ? trackCellClass(t) : 'col-track-rank-empty'"
                          class="text-center cell-data col-track-rank">
                        <span v-if="row.tracks[t]" class="cell-label">{{ row.tracks[t].rank }}</span>
                      </td>
                    </template>

                    <!-- Crests (2 columns) -->
                    <td v-for="ci in 2"
                        :key="`c_cr_${row.ilvl}_col${ci}`"
                        :class="row.crestCols[ci - 1] ? crestCellClass(row.crestCols[ci - 1].crestRank) : ''"
                        class="text-center cell-data-craft">
                      <template v-if="row.crestCols[ci - 1]">
                        <span class="cell-label">{{ row.crestCols[ci - 1].label }}</span>
                      </template>
                    </td>

                    <!-- Crafting (2 columns) -->
                    <td v-for="ci in 2"
                        :key="`c_cf_${row.ilvl}_col${ci}`"
                        :class="row.craftingCols[ci - 1] ? craftCellClass(row.craftingCols[ci - 1].rank) : ''"
                        class="text-center cell-data-craft">
                      <template v-if="row.craftingCols[ci - 1]">
                        <span class="cell-label">{{ row.craftingCols[ci - 1].label }}</span>
                        <span v-if="row.craftingCols[ci - 1].crestLabel" class="cell-label craft-crest-info" :class="'text--crest-' + row.craftingCols[ci - 1].crestRank">
                          {{ row.craftingCols[ci - 1].crestLabel }}
                        </span>
                      </template>
                    </td>

                    <!-- Raid -->
                    <td v-if="hasRaidTable"
                        :class="row.raid ? raidCellClasses(row.raid) : 'col-raid-empty'"
                        class="text-center cell-data-raid">
                      <span v-if="row.raid && row.raid.showLabel" class="cell-label raid-label">
                        {{ $t('item_level.raid_difficulty.' + row.raid.difficulty) }}
                      </span>
                    </td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Mythic+ -->
      <v-tab-item>
        <v-row class="pa-2">
          <v-col>
            <v-autocomplete :items="seasonsMPlus" item-text="name" item-value="id" v-model="currentSeasonMPlus" label="Season"/>
          </v-col>
        </v-row>
        <h3 class="ml-2 mt-2">Mythic+ Affix-Rotation</h3>
        <v-row class="pa-2 ma-2">
          <v-col v-for="index in [0,1,2,3]" :key="index">
            <v-row class="justify-center mb-2 font-weight-bold">
              {{$tc('information.affix_timespan', index)}}
            </v-row>
            <v-row v-for="(rotation, index) in getCurrentAffixes(getCurrentWeekNumber() + index, currentSeasonMPlus)" :key="`${currentSeasonMPlus}_${rotation}_${index}`">
              <v-col>
                <v-row class="justify-center">
                  <a :href="`https://www.wowhead.com/affix=${rotation}`" v-bind:data-wowhead="`domain=${$i18n.locale}`" target="_blank">
                    <img :src="`https://wow.zamimg.com/images/wow/icons/large/${(getAffixDetails(rotation)).media}.jpg`" style="width: 64px; height:64px;" alt="wow-icon">
                  </a>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <h3 class="ml-2 mt-2">{{ $t("m_plus.times") }}</h3>
        <v-row class="pa-2 ma-2">
          <v-data-table
              :headers="instance_timers_headers"
              :items="instance_timers_dungeons[currentSeasonMPlus]"
              item-key="name"
              hide-default-footer
          ></v-data-table>
        </v-row>
      </v-tab-item>

      <!-- Guild bank -->
      <v-tab-item>
        <!-- Sync-Dialog -->
        <v-dialog v-model="guildBankSyncDialog.open" max-width="600" dark>
          <v-card dark>
            <v-card-title class="headline">{{ $t('information.guild_bank.sync_dialog_title') }}</v-card-title>
            <v-card-text>
              <v-textarea
                  v-model="guildBankSyncDialog.jsonInput"
                  label="BagSync oder ZdPGuildBankExport Input"
                  dark auto-grow rows="8" class="mt-2 font-size-mono" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="guildBankSyncDialog.open = false">{{ $t('information.guild_bank.sync_dialog_cancel') }}</v-btn>
              <v-btn color="primary" :loading="guildBankSyncDialog.loading" @click="submitGuildBankSync">
                {{ $t('information.guild_bank.sync_dialog_confirm') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Expected-Amounts-Dialog -->
        <v-dialog v-model="guildBankExpectedDialog.open" max-width="700" dark scrollable>
          <v-card dark>
            <v-card-title class="headline">
              {{ $t('information.guild_bank.expected_dialog_title') }}
              <v-chip v-if="expectedHasUnsavedChanges" color="#e65100" small class="ml-3" label>
                <v-icon small left>mdi-content-save-alert</v-icon>
                {{ $t('information.guild_bank.expected_unsaved') }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <!-- Bestehende Einträge -->
              <div v-if="guildBankExpectedDialog.entries.length === 0" class="grey--text caption my-2">
                {{ $t('information.guild_bank.expected_no_entries') }}
              </div>
              <v-simple-table v-else dark dense class="mb-4">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t('information.auction.headers.item') }}</th>
                      <th class="text-left" style="width:120px">{{ $t('information.guild_bank.expected_amount') }}</th>
                      <th style="width:48px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in sortedExpectedDialogEntries" :key="entry.itemId">
                      <td>
                        <div style="display:flex;align-items:center;gap:8px;">
                          <div style="width: 25px; height: 25px; flex-shrink: 0;" v-if="getItemForId(entry.itemId) && getItemForId(entry.itemId).icon">
                            <a :href="getItemForId(entry.itemId).url" :data-wowhead="`item=${entry.itemId}&domain=${$i18n.locale}`" target="_blank">
                              <img :src="getItemForId(entry.itemId).icon" style="width: 25px; height: 25px;" alt="wow-icon">
                            </a>
                          </div>
                          <span>{{ getItemForId(entry.itemId) ? getItemForId(entry.itemId).label : entry.itemId }}</span>
                          <img v-if="getItemForId(entry.itemId) && getItemForId(entry.itemId).details && getItemForId(entry.itemId).details.craftingQualityTier"
                               :src="require(`@/assets/img/quality/${getItemForId(entry.itemId).details.craftingQualityTier}.png`)"
                               style="width:16px;height:16px;" :alt="`quality ${getItemForId(entry.itemId).details.craftingQualityTier}`">
                        </div>
                      </td>
                      <td>
                        <v-text-field v-model.number="entry.amount" type="number" min="1"
                                      dense hide-details style="max-width:100px;" />
                      </td>
                      <td>
                        <v-btn icon small color="red" @click="removeExpectedEntryById(entry.itemId)">
                          <v-icon small>mdi-minus-circle</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Neues Item hinzufügen -->
              <v-divider class="mb-3" />
              <v-row dense align="center">
                <v-col cols="7">
                  <v-autocomplete
                      v-model="guildBankExpectedDialog.newItemId"
                      :items="expectedDialogAvailableItems"
                      item-value="itemId"
                      item-text="label"
                      :label="$t('information.guild_bank.expected_add_item')"
                      dense hide-details outlined dark clearable>
                    <template v-slot:item="{ item }">
                      <div style="display:flex;align-items:center;gap:8px;">
                        <div style="width: 25px; height: 25px; flex-shrink: 0;">
                          <a :href="getItemForId(item.itemId).url" :data-wowhead="`item=${item.itemId}&domain=${$i18n.locale}`" target="_blank">
                            <img :src="item.icon" style="width: 25px; height: 25px;" alt="wow-icon">
                          </a>
                        </div>
                        <span>{{ item.label }}</span>
                        <img v-if="item.craftingQualityTier"
                             :src="require(`@/assets/img/quality/${item.craftingQualityTier}.png`)"
                             style="width:16px;height:16px;" :alt="`quality ${item.craftingQualityTier}`">
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="3">
                  <v-text-field v-model.number="guildBankExpectedDialog.newAmount" type="number" min="1"
                                :label="$t('information.guild_bank.expected_amount')"
                                dense hide-details outlined dark />
                </v-col>
                <v-col cols="2">
                  <v-btn color="primary" :disabled="!guildBankExpectedDialog.newItemId" @click="addExpectedEntry" block>
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="guildBankExpectedDialog.open = false">{{ $t('information.guild_bank.sync_dialog_cancel') }}</v-btn>
              <v-btn color="success" :loading="guildBankExpectedDialog.loading" @click="saveExpectedAmounts">
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('information.guild_bank.expected_save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-row class="pa-2 my-2 mx-0" align="center">
          <div class="d-flex flex-column mr-4 pl-1">
            <span v-if="auctions.guildBankLastSync" class="caption grey--text">
              <v-icon small class="mr-1">mdi-bank</v-icon>{{ $t('information.guild_bank.sync_last') }}: {{ formatTimestamp(auctions.guildBankLastSync) }}
            </span>
            <span v-if="auctions.lastUpdated" class="caption grey--text">
              <v-icon small class="mr-1">mdi-currency-usd</v-icon>{{ $t('information.auction.last_updated') }}: {{ formatTimestamp(auctions.lastUpdated) }}
            </span>
          </div>
          <v-spacer />
          <v-badge :value="expectedHasUnsavedChanges" color="#e65100" dot overlap>
            <v-btn @click="openExpectedDialog" color="secondary" class="mr-2" v-if="manageGuildBank">
              <v-icon left>mdi-playlist-edit</v-icon>
              {{ $t('information.guild_bank.expected_button') }}
            </v-btn>
          </v-badge>
          <v-btn @click="guildBankSyncDialog.open = true" color="secondary" class="mr-2" v-if="manageGuildBank">
            <v-icon left>mdi-bank-transfer</v-icon>
            {{ $t('information.guild_bank.sync_button') }}
          </v-btn>
          <v-btn @click="refreshAuctionsForCurrent($i18n.locale, true)" :loading="auctions.currentLoading" :disabled="!canRefresh" color="primary" class="mr-1">
            <v-icon>mdi-refresh</v-icon>
            {{ $t('information.auction.refresh') }}
          </v-btn>
        </v-row>
        <v-row v-if="auctions.currentResults.length > 0" class="pa-2 my-2 mx-0" dense align="center">
          <v-col cols="12" class="pb-1">
            <span class="caption grey--text text-uppercase">{{ $t('information.auction.filter') }}</span>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
                v-model="auctions.currentFilter.search"
                :label="$t('information.auction.search_label')"
                prepend-inner-icon="mdi-magnify"
                clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
                v-model="auctions.currentFilter.category"
                :items="auctionCategoryOptions"
                :label="$t('information.auction.headers.category')"
                clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
                v-model="auctions.currentFilter.priceCategory"
                :items="auctionPriceCategoryOptions"
                :label="$t('information.auction.headers.priceCategory')"
                clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
                v-model="auctions.currentFilter.expansion"
                :items="auctionExpansionOptions"
                :label="$t('information.auction.headers.expansion')"
                clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <v-checkbox
                v-model="auctions.currentFilter.onlyMaxQuality"
                :label="$t('information.auction.filter_max_quality')"
                hide-details dense dark class="mt-0 pt-0" />
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <v-checkbox
                v-model="auctions.currentFilter.onlyWithExpected"
                :label="$t('information.guild_bank.filter_only_expected')"
                hide-details dense dark class="mt-0 pt-0" />
          </v-col>
        </v-row>
        <v-row v-if="existingItemsTotalCost.total > 0 || missingItemsTotalCost.total > 0" class="px-2 pb-1 mx-0" dense align="center" justify="end">
          <v-col cols="auto">
            <div class="d-flex align-center flex-wrap justify-end" style="gap: 16px;">
              <div v-if="existingItemsTotalCost.total > 0">
                <span class="caption grey--text text-uppercase mr-2">{{ $t('information.guild_bank.existing_total_cost') }}:</span>
                <span style="display:inline-flex;align-items:center;gap:3px;">
                  <span class="font-weight-bold green--text">{{ existingItemsTotalCost.gold }}</span>
                  <img src="@/assets/img/currency/coin-gold.png" alt="g" style="width:16px;height:16px;position:relative;top:-1px;">
                  <span class="font-weight-bold green--text ml-1">{{ existingItemsTotalCost.silver }}</span>
                  <img src="@/assets/img/currency/coin-silver.png" alt="s" style="width:16px;height:16px;position:relative;top:-1px;">
                </span>
              </div>
              <div v-if="missingItemsTotalCost.total > 0">
                <span class="caption grey--text text-uppercase mr-2">{{ $t('information.guild_bank.missing_total_cost') }}:</span>
                <span style="display:inline-flex;align-items:center;gap:3px;">
                  <span class="font-weight-bold red--text">{{ missingItemsTotalCost.gold }}</span>
                  <img src="@/assets/img/currency/coin-gold.png" alt="g" style="width:16px;height:16px;position:relative;top:-1px;">
                  <span class="font-weight-bold red--text ml-1">{{ missingItemsTotalCost.silver }}</span>
                  <img src="@/assets/img/currency/coin-silver.png" alt="s" style="width:16px;height:16px;position:relative;top:-1px;">
                </span>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-data-table :items="filteredCurrentResults" :headers="auctionCurrentHeaders" :loading="auctions.currentLoading"
            :loading-text="$t('information.auction.loading')"
            :no-data-text="$t('information.auction.no_data_current')"
            hide-default-footer disable-pagination dense dark class="mt-2 auction-table"
            show-expand
            item-key="itemId"
            :expanded.sync="auctions.currentExpanded">
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <div style="display:flex; justify-content:flex-end;">
              <v-icon v-if="item.details && hasExpandableDetails(item)"
                      small style="cursor:pointer; transition: transform 0.2s; opacity: 0.75;"
                      :style="isExpanded ? 'transform: rotate(-20deg); opacity: 1; color: #ffd700;' : ''"
                      :color="isExpanded ? 'amber' : ''"
                      @click="expand(!isExpanded)">
                mdi-anvil
              </v-icon>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="auction-detail-panel">

                <!-- Reagenzien -->
                <template v-if="item.reagents && item.reagents.length > 0">
                  <div class="auction-reagents-title">{{ $t('information.auction.details.reagents') }}</div>

                  <!-- Pro Reagenz: Übersicht + Rang-Tabelle -->
                  <div v-for="(reagent, rIdx) in item.reagents" :key="rIdx" class="auction-reagent-block">
                    <!-- Kopfzeile: Menge + Name des ersten Rangs -->
                    <div class="auction-reagent-block-header">
                      <span class="auction-reagent-qty-badge">× {{ reagent.quantity }}</span>
                      <span class="auction-reagent-mat-name">
                        {{ reagent.tiers[0] && reagent.tiers[0].label ? reagent.tiers[0].label.replace(/\s+\d+$/, '') : $t('information.auction.details.reagent_unknown') }}
                      </span>
                    </div>

                    <!-- Rang-Tabelle -->
                    <div class="auction-reagent-tier-table">
                      <!-- Header -->
                      <div class="auction-reagent-tier-row auction-reagent-tier-header">
                        <div class="art-col-tier"></div>
                        <div class="art-col-name">{{ $t('information.auction.details.tier_item') }}</div>
                        <div class="art-col-price">{{ $t('information.auction.details.tier_unit_price') }}</div>
                        <div class="art-col-total">{{ $t('information.auction.details.tier_total') }}</div>
                      </div>
                      <!-- Zeilen pro Rang -->
                      <div v-for="(tier, tIdx) in reagent.tiers" :key="tier.itemId"
                           class="auction-reagent-tier-row"
                           :class="{ 'art-row--selected': getSelectedTierIndex(item.itemId, rIdx, reagent) === tIdx }"
                           :style="reagent.tiers.length > 1 ? 'cursor: pointer' : ''"
                           @click="reagent.tiers.length > 1 && selectTier(item.itemId, rIdx, tIdx)">
                        <!-- Rang-Icon: nur bei mehreren IDs -->
                        <div class="art-col-tier">
                          <template v-if="reagent.tiers.length > 1">
                            <img :src="require(`@/assets/img/quality/${tier.details && tier.details.craftingQualityTier ? tier.details.craftingQualityTier : tier.tier}.png`)"
                                 style="width: 14px; height: 14px;" :alt="`rank ${tier.tier}`">
                          </template>
                        </div>
                        <!-- Icon + Name -->
                        <div class="art-col-name">
                          <img v-if="tier.icon" :src="tier.icon" style="width: 18px; height: 18px; border-radius: 2px;" alt="">
                          <a :href="tier.url" target="_blank"
                             style="color: rgba(255,255,255,0.85); text-decoration: none;"
                             @click.stop>
                            {{ tier.label }}
                          </a>
                        </div>
                        <!-- Einzelpreis -->
                        <div class="art-col-price">
                          <template v-if="tier.price && tier.price.total > 0">
                            <span>{{ tier.price.gold }}</span>
                            <img src="@/assets/img/currency/coin-gold.png" style="width: 16px; height: 16px;" alt="g">
                            <span>{{ tier.price.silver }}</span>
                            <img src="@/assets/img/currency/coin-silver.png" style="width: 16px; height: 16px;" alt="s">
                          </template>
                          <span v-else class="auction-detail-label">—</span>
                        </div>
                        <!-- Gesamtpreis -->
                        <div class="art-col-total">
                          <template v-if="tier.totalPrice && tier.totalPrice.total > 0">
                            <span>{{ tier.totalPrice.gold }}</span>
                            <img src="@/assets/img/currency/coin-gold.png" style="width: 16px; height: 16px;" alt="g">
                            <span>{{ tier.totalPrice.silver }}</span>
                            <img src="@/assets/img/currency/coin-silver.png" style="width: 16px; height: 16px;" alt="s">
                          </template>
                          <span v-else class="auction-detail-label">—</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Craft-Kosten: unterhalb aller Reagenzientabellen, rechtsbündig -->
                  <div v-if="calcCraftCost(item)" class="auction-craftcost-row">
                    <div class="auction-craftcost-box">
                      <span class="auction-craftcost-label">{{ $t('information.auction.details.craft_cost_total') }}</span>
                      <div class="auction-craftcost-value">
                        <span>{{ calcCraftCost(item).gold }}</span>
                        <img src="@/assets/img/currency/coin-gold.png" style="width: 18px; height: 18px;" alt="g">
                        <span>{{ calcCraftCost(item).silver }}</span>
                        <img src="@/assets/img/currency/coin-silver.png" style="width: 18px; height: 18px;" alt="s">
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </td>
          </template>
          <template v-slot:item.category="{ item }">
            {{$t('information.auction.category.' + item.category)}}
          </template>
          <template v-slot:item.label="{ item }">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="height: 25px; flex-shrink: 0;">
                <a :href="item.url" :data-wowhead="`item=${item.itemId}&domain=${$i18n.locale}`" target="_blank">
                  <img :src="item.icon" style="width: 25px; height: 25px;" alt="wow-icon">
                </a>
              </div>
              <span>{{ item.label }}</span>
              <img v-if="item.details && item.details.craftingQualityTier"
                   :src="require(`@/assets/img/quality/${item.details.craftingQualityTier}.png`)"
                   style="width: 16px; height: 16px;" :alt="`quality ${item.details.craftingQualityTier}`">
            </div>
          </template>
          <template v-slot:item.priceCategory="{ item }">
            <span v-if="item.priceCategory">
              {{$t('information.auction.priceCategory.' + item.priceCategory)}}
            </span>
            <span v-else>-</span>
          </template>
          <template v-slot:item.priceTotal="{ item }">
            <span v-if="item.price && item.price.total > 0">
              <span style="display: inline-flex; align-items: center; gap: 8px;">
                <span style="display: inline-flex; align-items: center; gap: 2px;" >
                {{ item.price?.gold }}<img src="@/assets/img/currency/coin-gold.png" alt="g" style="width: 18px; height: 18px; position: relative; top: -2px;">
                </span>
                <span style="display: inline-flex; align-items: center; gap: 2px;">
                  {{ item.price?.silver }}<img src="@/assets/img/currency/coin-silver.png" alt="s" style="width: 18px; height: 18px; position: relative; top: -2px;">
                </span>
              </span>
            </span>
            <span v-else></span>
          </template>
          <template v-slot:item.missingTotalPrice="{ item }">
            <span v-if="item.missingTotalPrice > 0" style="display: inline-flex; align-items: center; gap: 2px;">
              <span>{{ Math.floor(item.missingTotalPrice).toLocaleString($i18n.locale === 'de' ? 'de-DE' : 'en-US') }}</span>
              <img src="@/assets/img/currency/coin-gold.png" alt="g" style="width: 18px; height: 18px; position: relative; top: -2px;">
              <span>{{ Math.round((item.missingTotalPrice - Math.floor(item.missingTotalPrice)) * 100).toString().padStart(2, '0') }}</span>
              <img src="@/assets/img/currency/coin-silver.png" alt="s" style="width: 18px; height: 18px; position: relative; top: -2px;">
            </span>
            <span v-else></span>
          </template>
          <template v-slot:item.available="{ item }">
            <v-icon :color="item.available ? 'green' : 'red'" small>{{ item.available ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          </template>
          <template v-slot:item.details.expansion="{ item }">
            <span v-if="item.details && item.details.expansion != null"
                  class="expansion-badge"
                  :title="expansionLabel(item.details.expansion)">
              {{ expansionAbbr(item.details.expansion) }}
            </span>
            <span v-else class="expansion-badge expansion-badge--empty">—</span>
          </template>
          <template v-slot:item.guildBankExisting="{ item }">
            <span :class="item.guildBankExisting > 0 ? 'green--text' : 'grey--text'">
              {{ item.guildBankExisting ?? 0 }}
            </span>
          </template>
          <template v-slot:item.guildBankExpected="{ item }">
            <span class="grey--text">{{ item.guildBankExpected ?? 0 }}</span>
          </template>
          <template v-slot:item.guildBankMissing="{ item }">
            <span :class="item.guildBankMissing > 0 ? 'red--text' : 'green--text'">
              {{ item.guildBankMissing ?? 0 }}
            </span>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- Auctions - Search -->
      <v-tab-item>
        <v-row class="pa-2 my-2 mx-0" align="center">
          <v-text-field :label="$t('information.auction.search_label')" v-model="auctions.search" @keyup.enter="getAuctionsForSearch($i18n.locale)" dense clearable hide-details />
          <v-btn @click="getAuctionsForSearch($i18n.locale)" :loading="auctions.searchLoading" color="primary" class="ml-2">
            <v-icon>mdi-magnify</v-icon>
            {{ $t('information.auction.search') }}
          </v-btn>
          <v-btn @click="refreshAuctionsForSearch($i18n.locale)" :loading="auctions.searchLoading" :disabled="!canRefresh" class="mx-2">
            <v-icon left>mdi-refresh</v-icon>
            {{ $t('information.auction.refresh') }}
          </v-btn>
          <span class="caption grey--text" v-if="auctions.lastUpdated">
              {{ $t('information.auction.last_updated') }}: {{ formatTimestamp(auctions.lastUpdated) }}
            </span>
        </v-row>

        <v-row v-if="auctions.searchResults.length > 0" class="pa-2 mx-0 mt-1" dense align="center">
          <v-col cols="12" class="pb-1">
            <span class="caption grey--text text-uppercase">{{ $t('information.auction.filter') }}</span>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field v-model="auctions.searchFilter.search" :label="$t('information.auction.search_label')" prepend-inner-icon="mdi-magnify" clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select v-model="auctions.searchFilter.priceCategory" :items="auctionSearchPriceCategoryOptions" :label="$t('information.auction.headers.priceCategory')" clearable hide-details dense outlined dark />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select v-model="auctions.searchFilter.expansion" :items="auctionSearchExpansionOptions" :label="$t('information.auction.headers.expansion')" clearable hide-details dense outlined dark />
          </v-col>
        </v-row>

        <v-data-table :items="filteredSearchResults" :headers="auctionHeaders" :loading="auctions.searchLoading"
            :loading-text="$t('information.auction.loading')"
            :no-data-text="$t('information.auction.no_data_search')"
            hide-default-footer disable-pagination dense dark class="mt-2 auction-table">
          <template v-slot:item.label="{ item }">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="width: 25px; height: 25px; flex-shrink: 0;">
                <a :href="item.url" :data-wowhead="`item=${item.itemId}&domain=${$i18n.locale}`" target="_blank">
                  <img :src="item.icon" style="width: 25px; height: 25px;" alt="wow-icon">
                </a>
              </div>
              <span>{{ item.label }}</span>
              <img v-if="item.details && item.details.craftingQualityTier"
                   :src="require(`@/assets/img/quality/${item.details.craftingQualityTier}.png`)"
                   style="width: 16px; height: 16px;" :alt="`quality ${item.details.craftingQualityTier}`">
            </div>
          </template>
          <template v-slot:item.priceCategory="{ item }">
            <span v-if="item.priceCategory">
              {{$t('information.auction.priceCategory.' + item.priceCategory)}}
            </span>
            <span v-else>-</span>
          </template>
          <template v-slot:item.priceTotal="{ item }">
            <span v-if="item.price && item.price.total > 0">
              <span style="display: inline-flex; align-items: center; gap: 8px;">
                <span style="display: inline-flex; align-items: center; gap: 2px;" >
                {{ item.price?.gold }}<img src="@/assets/img/currency/coin-gold.png" alt="g" style="width: 18px; height: 18px; position: relative; top: -2px;">
                </span>
                <span style="display: inline-flex; align-items: center; gap: 2px;">
                  {{ item.price?.silver }}<img src="@/assets/img/currency/coin-silver.png" alt="s" style="width: 18px; height: 18px; position: relative; top: -2px;">
                </span>
              </span>
            </span>
            <span v-else></span>
          </template>
          <template v-slot:item.available="{ item }">
            <v-icon :color="item.available ? 'green' : 'red'" small>{{ item.available ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          </template>
          <template v-slot:item.details.expansion="{ item }">
            <span v-if="item.details && item.details.expansion != null"
                  class="expansion-badge"
                  :title="expansionLabel(item.details.expansion)">
              {{ expansionAbbr(item.details.expansion) }}
            </span>
            <span v-else class="expansion-badge expansion-badge--empty">—</span>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- Darkmoon faire  -->
      <v-tab-item key="general-tab" class="ma-2">
        <h3 class="ml-2">{{ $t('information.darkmoon_faire.title') }}</h3>
        <v-data-table :items="dark_moon_fairy.data" :headers="darkMoonFairyHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.quest="{ item }">
            <a :href='`https://www.wowhead.com/${$i18n.locale}/quest=${item.quest.id}`'>{{ item.quest.name }}</a>
          </template>
          <template v-slot:item.materials="{ item }">
            <div v-for="material in item.materials" :key="material.id">
              <span v-if="material.id !== null">
                {{material.qty}}x <a :href='`https://www.wowhead.com/${$i18n.locale}/item=${material.id}`'>{{ material.name }}</a>
              </span>
              <span v-else>
                {{material.qty}}x {{ material.name }}
              </span>
            </div>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- Race / Class combinations -->
      <v-tab-item key="race-class-tab" class="ma-2">
        <h3 class="ml-2 mb-3">{{ $t('information.race_class.title') }}</h3>
        <div class="rc-table-wrap">
          <v-simple-table dark dense class="race-class-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="rc-col-class">{{ $t('information.race_class.col_class') }}</th>
                  <th v-for="race in raceClassRaces" :key="race" class="rc-col-race text-center">{{ $t('information.race_class.races.' + race) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(combos, className) in raceClassCombination" :key="className">
                  <td class="rc-col-class font-weight-medium">{{ $t('information.race_class.classes.' + className) }}</td>
                  <td v-for="race in raceClassRaces" :key="race" class="text-center rc-cell">
                    <v-icon v-if="getRaceValue(combos, race) === true"  small color="green">mdi-check-circle</v-icon>
                    <v-icon v-else-if="getRaceValue(combos, race) === false" small color="red">mdi-close-circle</v-icon>
                    <span v-else></span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import constants from "../util/Constants";
import DataService from "@/services/data.service";

export default {
  name: "Information",
  data() {
    return {
      tab: 0,
      currentSeasonItemLevel: 'MD2',
      currentSeasonMPlus: 'MD2',
      seasons: [
      { id: 'WW1', name: 'The War Within - Season 1', showItemLevel: false, showMPlus: true },
      { id: 'WW2', name: 'The War Within - Season 2' , showItemLevel: false, showMPlus: true},
      { id: 'WW3', name: 'The War Within - Season 3' , showItemLevel: true, showMPlus: true},
      { id: 'MD1', name: 'Midnight - Season 1' , showItemLevel: true, showMPlus: true},
      { id: 'MD2', name: 'Midnight - Season 2' , showItemLevel: true, showMPlus: true}
      ],
      dark_moon_fairy: {
        headers: [],
        data: [
          { profession: "Alchemie",         quest: { id: 29506, name: "Eine prickelnde Mischung" },                             materials: [{ qty: 5, id: 1645, name: "Mondbeerensaft" }] },
          { profession: "Angeln",           quest: { id: 29513, name: "Leckerer Fisch im Schlafrock" },                         materials: null },
          { profession: "Archäologie",      quest: { id: 29507, name: "Spaß für die Kleinen" },                                 materials: [{ qty: 15, id: null, name: "Fossil-Fragmente" }] },
          { profession: "Bergbau",          quest: { id: 29518, name: "Wiederbewaffnung, Wiederverwendung, Wiederverwertung" }, materials: null },
          { profession: "Ingenieurskunst",  quest: { id: 29511, name: "Panzerreparatur" },                                      materials: null },
          { profession: "Inschriftenkunde", quest: { id: 29515, name: "Ein Blick in die Zukunft" },                             materials: [{ qty: 5, id: 39354, name: "Leichtes Pergament" }] },
          { profession: "Juwelenschleifen", quest: { id: 29516, name: "Jahrmarkt der Eitelkeiten" },                            materials: null },
          { profession: "Kochkunst",        quest: { id: 29509, name: "Wie der Frosch knusprig wird" },                         materials: [{ qty: 5, id: 30817, name: "Herkömmliches Mehl" }] },
          { profession: "Kräuterkunde",     quest: { id: 29514, name: "Heilkräuter" },                                          materials: null },
          { profession: "Kürschnerei",      quest: { id: 29519, name: "Gerbt meine Häute" },                                    materials: null },
          { profession: "Lederverarbeitung",quest: { id: 29517, name: "Begehrte Preise" },                                      materials: [{ qty: 10, id: 6529, name: "Glänzendes Schmuckstück" }, { qty: 5, id: 2320, name: "Grober Faden" }, { qty: 5, id: 6260, name: "Blauer Farbstoff" }] },
          { profession: "Schmiedekunst",    quest: { id: 29508, name: "Baby braucht zwei Paar Schuhe" },                        materials: null },
          { profession: "Schneiderei",      quest: { id: 29520, name: "Banner, überall Banner!" },                              materials: [{ qty: 1, id: 2320, name: "Grober Faden" }, { qty: 1, id: 6260, name: "Blauer Farbstoff" }, { qty: 1, id: 2604, name: "Roter Farbstoff" }] },
          { profession: "Verzauberkunst",   quest: { id: 59510, name: "Auch Abfall kann nützlich sein" },                       materials: null }
        ]
      },
      instance_timers_headers: [
        { text: 'Dungeon', value: 'name' },
        { text: 'Timer', value: 'timer' },
        { text: '+2', value: 'plus2' },
        { text: '+3', value: 'plus3' },
      ],
      instance_timers_dungeons: {
        WW1: [
          { name: "Ara-Kara, Stadt der Echos", timer: "30:00"},
          { name: "Stadt der Fäden", timer: "35:00"},
          { name: "Die Morgenbringer", timer: "30:00"},
          { name: "Das Steingewölbe", timer: "33:00"},
          { name: "Die Nebel von Tirna Scithe", timer: "30:00"},
          { name: "Die Belagerung von Boralus", timer: "34:00"},
          { name: "Grim Batol", timer: "36:00", plus2: "29:12"},
          { name: "Die Nekrotische Schneise", timer: "32:00"}
        ],
        WW2: [
          { name: "Cinderbrew Meadery", timer: "33:00"},
          { name: "Darkflame Cleft", timer: "33:00"},
          { name: "The Rookery", timer: "31:00"},
          { name: "Priory of the Sacred Flame", timer: "31:00"},
          { name: "Operation: Floodgate", timer: "33:00"},
          { name: "The MOTHERLODE!!", timer: "39:00"},
          { name: "Theater of Pain", timer: "34:00"},
          { name: "Operation: Mechagon - Workshop", timer: "32:00"}
        ],
        WW3: [
          { name: "Eco-Dome Al'dani", timer: "31:00"},
          { name: "Ara-Kara, City of Echoes", timer: "30:00"},
          { name: "The Dawnbreaker", timer: "31:00"},
          { name: "Priory of the Sacred Flame", timer: "32:30"},
          { name: "Operation: Floodgate", timer: "33:00"},
          { name: "Halls of Atonement", timer: "31:00"},
          { name: "Tazavesh: Streets of Wonder", timer: "39:00"},
          { name: "Tazavesh: So'leah's Gambit", timer: "30:00"}
        ],
        MD1: [
          { name: "Magisters' Terrace", timer: "34:00"},
          { name: "Maisara Caverns", timer: "33:00"},
          { name: "Nexus-Point Xenas", timer: "30:00" },
          { name: "Windrunner Spire", timer: "33:30" },
          { name: "Algeth'ar Academy", timer: "29:00" },
          { name: "Seat of the Triumvirate", timer: "34:00" },
          { name: "Pit of Saron", timer: "30:00" },
          { name: "Skyreach", timer: "28:00" }
        ],
        MD2: [
          { name: "Altar of Fangs", timer: "30:00" },
          { name: "Murder Row", timer: "34:00" },
          { name: "Den of Nalorakk", timer: "32:00" },
          { name: "The Blinding Vale", timer: "33:00" },
          { name: "Voidscar Arena", timer: "30:00" },
          { name: "King's Rest", timer: "33:00" },
          { name: "Temple of Sethraliss", timer: "33:00" },
          { name: "Ruby Life Pools", timer: "29:00" }
        ]
      },
      auctions: {
        search: "",
        lastUpdated: null,
        guildBankLastSync: null,
        currentResults: [],
        currentLoading: false,
        currentLastUpdated: null,
        currentFilter: {
          search: "",
          category: null,
          priceCategory: null,
          expansion: null,
          onlyMaxQuality: false,
          onlyWithExpected: true
        },
        currentExpanded: [],
        selectedTiers: {},
        searchResults: [],
        searchLoading: false,
        searchLastUpdated: null,
        searchFilter: {
          search: "",
          priceCategory: null,
          expansion: null
        }
      },
      guildBankSyncDialog: {
        open: false,
        loading: false,
        jsonInput: ""
      },
      guildBankExpectedDialog: {
        open: false,
        loading: false,
        entries: [],        // [{ itemId: '241325', amount: 50 }, ...]
        newItemId: null,
        newAmount: 1,
        initialized: false,  // true sobald einmal vom Server geladen
        savedSnapshot: []    // Snapshot der zuletzt gespeicherten Einträge
      },
      raceClassCombination: {
        "druid":       [{"bloodelf": null},  {"orc": null},  {"undead": null},  {"goblin": null},  {"tauren": true},  {"pandaren": null},  {"troll": true}],
        "hunter":      [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "warrior":     [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "rogue":       [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": false}, {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "mage":        [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": false}, {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "priest":      [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "warlock":     [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "shaman":      [{"bloodelf": null},  {"orc": true},  {"undead": null},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "paladin":     [{"bloodelf": true},  {"orc": null},  {"undead": null},  {"goblin": null},  {"tauren": true},  {"pandaren": null},  {"troll": null}],
        "monk":        [{"bloodelf": true},  {"orc": true},  {"undead": false}, {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "deathknight": [{"bloodelf": true},  {"orc": true},  {"undead": true},  {"goblin": true},  {"tauren": true},  {"pandaren": true},  {"troll": true}],
        "demonhunter": [{"bloodelf": true},  {"orc": null},  {"undead": null},  {"goblin": null},  {"tauren": null},  {"pandaren": null},  {"troll": null}]
      },
      EU_START_WEEK: 1774411200,
      SECONDS_PER_DAY: 24*60*60,
      EU_DELAY: 0,
      affixMapping: {
        3: {   name: "Volcanic",                       media: "spell_shaman_lavasurge",                        description: ""},
        6: {   name: "Raging",                         media: "ability_warrior_focusedrage",                   description: ""},
        7: {   name: "Bolstering",                     media: "ability_warrior_battleshout",                   description: ""},
        8: {   name: "Sanguine",                       media: "spell_shadow_bloodboil",                        description: ""},
        9: {   name: "Tyrannical",                     media: "achievement_boss_archaedas",                    description: "Bosses have 25% more health. Bosses and their minions inflict up to 15% increased damage."},
        10: {  name: "Fortified",                      media: "ability_toughness",                             description: ""},
        11: {  name: "Bursting",                       media: "ability_ironmaidens_whirlofblood",              description: ""},
        123: { name: "Spiteful",                       media: "spell_holy_prayerofshadowprotection",           description: ""},
        124: { name: "Storming",                       media: "spell_nature_cyclone",                          description: ""},
        134: { name: "Inspiring",                      media: "inv_misc_root_01",                              description: ""},
        135: { name: "Afflicted",                      media: "spell_misc_emotionsad",                         description: ""},
        136: { name: "Incorporeal",                    media: "achievement_boss_anomalus",                     description: ""},
        147: { name: "Xal'atath's Guile",              media: "ability_racial_chillofnight",                   description: "Xal'atath betrays players, revoking her bargains while making deaths subtract 15 seconds from time remaining."},
        148: { name: "Xal'atath's Bargain: Ascendant", media: "inv_nullstone_cosmicvoid",                      description: "While in combat, Xal'atath rains down orbs of cosmic energy that empower enemies or players."},
        152: { name: "Challenger's Peril",             media: "achievement_challengemode_everbloom_hourglass", description: "The timer is increased by 90 seconds, but dying subtracts 15 seconds from time remaining."},
        158: { name: "Xal'atath's Bargain: Voidbound", media: "inv_cosmicvoid_buff",                           description: "While in combat, Xal'atath summons a Void Emissary that empowers nearby enemies."},
        159: { name: "Xal'atath's Bargain: Oblivion",  media: "spell_priest_void-blast",                       description: "While in combat, Xal'atath manifests crystals from the void that can be absorbed by enemies or players."},
        160: { name: "Xal'atath's Bargain: Devour",    media: "inv_ability_voidweaverpriest_entropicrift",     description: "While in combat, Xal'atath tears open rifts that devour the essence of players."},
        162: { name: "Xal'atath's Bargain: Pulsar",    media: "inv_cosmicvoid_nova",                           description: "While in combat, Xal'atath summons pulsars that orbit players."},
      },
      affixesTurn: {
        WW1: [[9, 9, 9], [10, 10, 10]],
        WW2: [
          [162, 9, 10],
          [148, 9],
          [159, 10, 9],
          [160, 9, 10, 7, 8, 123, 124],
        ],
        WW3: [
          [158, 9, 10, 147],
          [148, 9, 10, 147],
          [162, 9, 10, 147],
          [160, 9, 10, 147],
        ],
        MD1: [
          [148, 9, 10, 147],
          [162, 9, 10, 147],
          [160, 9, 10, 147],
          [158, 9, 10, 147]
        ],
        MD2: [
          [148, 9, 10, 147], // Ascendant + Tyrannical + Fortified + Guile
          [162, 9, 10, 147], // Pulsar    + Tyrannical + Fortified + Guile
          [160, 9, 10, 147], // Devour    + Tyrannical + Fortified + Guile
          [158, 9, 10, 147]  // Voidbound + Tyrannical + Fortified + Guile
        ]
      },
    }
  },

  mounted() {
    const hashTab = this.getTabIndexFromHash(this.$route.hash);
    if (hashTab !== null) {
      this.tab = hashTab;
    }
    DataService.getGuildBankStatus().then(res => {
      if (res?.guildBankLastSync) {
        this.auctions.guildBankLastSync = res.guildBankLastSync;
      }
    });
    this.refreshAuctionsForCurrent(this.$i18n.locale, false);
    this.enhanceMplusTimers();
  },

  methods: {
    enhanceMplusTimers(){
      const toSeconds = (mmss) => {
        const [m, s] = mmss.split(":").map(Number);
        return m * 60 + s;
      };

      const toMMSS = (totalSeconds) => {
        const m = Math.floor(totalSeconds / 60);
        const s = totalSeconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      };

      const withThresholds = (dungeons) => dungeons.map((d) => {
        const timerSeconds = toSeconds(d.timer);
        const plus2Seconds = Math.floor(timerSeconds * 0.8); // 20% Restzeit
        const plus3Seconds = Math.floor(timerSeconds * 0.6); // 40% Restzeit

        return {
          ...d,
          timerSeconds,
          plus2: toMMSS(plus2Seconds),
          plus2Seconds,
          plus3: toMMSS(plus3Seconds),
          plus3Seconds
        };
      });

      Object.keys(this.instance_timers_dungeons).forEach(seasonKey => {
        let seasonEntry = this.instance_timers_dungeons[seasonKey];
        this.instance_timers_dungeons[seasonKey] = withThresholds(seasonEntry);
      });
    },
    getTabIndexFromHash(hash) {
      const map = {
        '#item_level':    0,
        '#mplus':         1,
        '#guild_bank':    2,
        '#auction_search':3,
        '#darkmoon_faire':4,
        '#race_class':    5,
      };
      return map[hash] ?? null;
    },
    onTabChange(index) {
      const hashes = ['#item_level', '#mplus', '#guild_bank', '#auction_search', '#darkmoon_faire', '#race_class'];
      const newHash = hashes[index] ?? '';
      if (this.$route.hash !== newHash) {
        this.$router.replace({ hash: newHash }).catch(() => {});
      }
    },
    getRaceValue(combos, race) {
      const entry = combos.find(obj => Object.prototype.hasOwnProperty.call(obj, race));
      if (!entry) return null;
      return entry[race];
    },
    refreshAuctionsForCurrent(locale, doRefresh = false){
      this.auctions.currentLoading = true;
      const fetchData = () => DataService.getAuctionDetailsForCurrentSeasonItems(locale);
      const pipeline = doRefresh
        ? DataService.refreshAuctionCache(locale).then(fetchData)
        : fetchData();
      pipeline.then(res => {
        this.auctions.currentResults = this.normalizeAuctionItems(res?.items || []);
        this.auctions.lastUpdated = res?.lastUpdated || null;
        this.auctions.guildBankLastSync = res?.guildBankLastSync || null;
      }).catch(() => {
        this.auctions.currentResults = [];
      }).finally(() => {
        this.auctions.currentLoading = false;
      });
    },
    submitGuildBankSync() {
      const parse = (rawJson) => {
        const trimmed = rawJson.trim();

        // Format 1: JSON-Objekt (flach oder Addon-Format mit "items"-Property)
        if (trimmed.startsWith("{")) {
          let parsed;
          try {
            parsed = JSON.parse(trimmed);
          } catch (e) {
            throw new Error("Invalid JSON object format");
          }
          if (typeof parsed !== "object" || Array.isArray(parsed)) {
            throw new Error("Expected a plain JSON object");
          }
          // Addon-Format: { "lastSync": 123, "items": { "itemId": amount } }
          const itemMap = (parsed.items && typeof parsed.items === "object" && !Array.isArray(parsed.items))
            ? parsed.items
            : parsed;
          const result = {};
          for (const [id, amount] of Object.entries(itemMap)) {
            const amt = parseInt(amount, 10);
            if (!Number.isFinite(amt)) continue;
            if (!result[id]) result[id] = 0;
            result[id] += amt;
          }
          return result;
        }

        // Format 3: Lua SavedVariables – ZdPGuildBankExportDB = { ["items"] = { [id] = qty, ... }, ... }
        // Erkennung: enthält ["items"] oder beginnt mit ZdPGuildBankExportDB
        if (trimmed.includes('["items"]') || trimmed.startsWith('ZdPGuildBankExportDB')) {
          // Items-Block extrahieren: alles zwischen ["items"] = { ... }
          const itemsMatch = trimmed.match(/\["items"\]\s*=\s*\{([^}]*)\}/s);
          if (!itemsMatch) throw new Error("Lua format: [\"items\"] block not found");
          const itemsBlock = itemsMatch[1];
          // Einträge parsen: [123456] = 42,
          const result = {};
          const entryRe = /\[(\d+)\]\s*=\s*(\d+)/g;
          let m;
          while ((m = entryRe.exec(itemsBlock)) !== null) {
            const id  = m[1];
            const amt = parseInt(m[2], 10);
            if (!Number.isFinite(amt)) continue;
            result[id] = (result[id] || 0) + amt;
          }
          return result;
        }

        // Format 2: Baginator Lua-Format
        // 1. "['tabs'] =" vorne entfernen
        let code = trimmed
          .replace(/^\s*\["tabs"]\s*=\s*/, "")
          .trim();

        // 2. Lua-Klammern zu JS-Klammern
        code = code
          .replace(/{/g, "[")
          .replace(/}/g, "]");

        // Trailing Kommas entschärfen
        code = code.replace(/,\s*([\]}])/g, "$1");

        // 3. Als JS-Ausdruck auswerten
        const tabs = Function('"use strict"; return ' + code + ';')();

        if (!Array.isArray(tabs)) {
          throw new Error("Parsed structure is not an array");
        }

        // 4. Items aggregieren
        const result = {};
        for (const tab of tabs) {
          if (!Array.isArray(tab)) continue;
          for (const entry of tab) {
            if (typeof entry !== "string") continue;
            const [id, amountRaw] = entry.split(";");
            const amount = amountRaw ? parseInt(amountRaw, 10) : 1;
            if (!Number.isFinite(amount)) continue;
            if (!result[id]) result[id] = 0;
            result[id] += amount;
          }
        }
        return result;
      };

      let parsed;
      try {
        parsed = parse(this.guildBankSyncDialog.jsonInput);
      } catch (e) {
        this.$toast?.error(this.$t('information.guild_bank.sync_error_parse'));
        return;
      }

      this.guildBankSyncDialog.loading = true;
      DataService.syncGuildBank(parsed, this.$i18n.locale).then(res => {
        if (res) {
          this.auctions.currentResults = this.normalizeAuctionItems(res?.items || []);
          this.auctions.lastUpdated = res?.lastUpdated || null;
          this.auctions.guildBankLastSync = res?.guildBankLastSync || null;
          this.guildBankSyncDialog.open = false;
          this.guildBankSyncDialog.jsonInput = '';
          this.$toast?.success(this.$t('information.guild_bank.sync_success'));
        }
      }).finally(() => {
        this.guildBankSyncDialog.loading = false;
      });
    },
    submitGuildBankSync2() {
      const parse = (rawJson) => {
        let code = rawJson
            .replace(/^\s*\["tabs"]\s*=\s*/, "")
            .trim();

        // 2. Lua-Klammern zu JS-Klammern
        code = code
            .replace(/{/g, "[")
            .replace(/}/g, "]");

        // Trailing Kommas entschärfen
        code = code.replace(/,\s*([\]}])/g, "$1");

        // 3. Als JS-Ausdruck auswerten
        const tabs = Function('"use strict"; return ' + code + ';')();

        if (!Array.isArray(tabs)) {
          throw new Error("Parsed structure is not an array");
        }

        // 4. Items aggregieren
        const result = {};
        for (const tab of tabs) {
          if (!Array.isArray(tab)) continue;
          for (const entry of tab) {
            if (typeof entry !== "string") continue;
            const [id, amountRaw] = entry.split(";");
            const amount = amountRaw ? parseInt(amountRaw, 10) : 1;
            if (!Number.isFinite(amount)) continue;
            if (!result[id]) result[id] = 0;
            result[id] += amount;
          }
        }
        return result;
      };

      let parsed;
      try {
        parsed = parse(this.guildBankSyncDialog.jsonInput);
      } catch (e) {
        this.$toast?.error(this.$t('information.guild_bank.sync_error_parse'));
        return;
      }

      this.guildBankSyncDialog.loading = true;
      DataService.syncGuildBank(parsed, this.$i18n.locale).then(res => {
        if (res) {
          this.auctions.currentResults = this.normalizeAuctionItems(res?.items || []);
          this.auctions.lastUpdated = res?.lastUpdated || null;
          this.auctions.guildBankLastSync = res?.guildBankLastSync || null;
          this.guildBankSyncDialog.open = false;
          this.guildBankSyncDialog.jsonInput = '';
          this.$toast?.success(this.$t('information.guild_bank.sync_success'));
        }
      }).finally(() => {
        this.guildBankSyncDialog.loading = false;
      });
    },
    getItemForId(itemId) {
      return this.auctions.currentResults.find(i => String(i.itemId) === String(itemId)) || null;
    },
    openExpectedDialog() {
      // Nur beim ersten Öffnen aus den currentResults initialisieren –
      // danach bleiben ungespeicherte Änderungen erhalten.
      if (!this.guildBankExpectedDialog.initialized) {
        const entries = this.auctions.currentResults
          .filter(i => (i.guildBankExpected ?? 0) > 0)
          .map(i => ({ itemId: String(i.itemId), amount: i.guildBankExpected }));
        this.guildBankExpectedDialog.entries = entries;
        this.guildBankExpectedDialog.savedSnapshot = entries.map(e => ({ ...e }));
        this.guildBankExpectedDialog.newItemId = null;
        this.guildBankExpectedDialog.newAmount = 1;
        this.guildBankExpectedDialog.initialized = true;
      }
      this.guildBankExpectedDialog.open = true;
    },
    addExpectedEntry() {
      const id = String(this.guildBankExpectedDialog.newItemId);
      if (!id) return;
      // nicht doppelt hinzufügen
      if (this.guildBankExpectedDialog.entries.find(e => e.itemId === id)) return;
      this.guildBankExpectedDialog.entries.push({
        itemId: id,
        amount: Math.max(1, Number(this.guildBankExpectedDialog.newAmount) || 1)
      });
      this.guildBankExpectedDialog.newItemId = null;
      this.guildBankExpectedDialog.newAmount = 1;
    },
    removeExpectedEntry(idx) {
      this.guildBankExpectedDialog.entries.splice(idx, 1);
    },
    removeExpectedEntryById(itemId) {
      const idx = this.guildBankExpectedDialog.entries.findIndex(e => e.itemId === String(itemId));
      if (idx !== -1) this.guildBankExpectedDialog.entries.splice(idx, 1);
    },
    saveExpectedAmounts() {
      const expectedAmounts = {};
      this.guildBankExpectedDialog.entries.forEach(e => {
        expectedAmounts[e.itemId] = Math.max(0, Number(e.amount) || 0);
      });
      this.guildBankExpectedDialog.loading = true;
      DataService.updateGuildBankExpected(expectedAmounts).then(res => {
        if (res) {
          // currentResults aktualisieren
          this.auctions.currentResults.forEach(item => {
            const exp = expectedAmounts[String(item.itemId)] ?? 0;
            item.guildBankExpected = exp;
            item.guildBankMissing = Math.max(0, exp - (item.guildBankExisting ?? 0));
            item.priceTotal = item.price?.total ?? 0;
            item.missingTotalPrice = item.guildBankMissing * item.priceTotal;
          });
          // Nach dem Speichern initialized zurücksetzen, damit beim nächsten
          // Öffnen die frischen Daten vom Server / currentResults geladen werden.
          this.guildBankExpectedDialog.savedSnapshot = this.guildBankExpectedDialog.entries.map(e => ({ ...e }));
          this.guildBankExpectedDialog.initialized = false;
          this.guildBankExpectedDialog.open = false;
          this.$toast?.success(this.$t('information.guild_bank.expected_saved'));
        }
      }).finally(() => {
        this.guildBankExpectedDialog.loading = false;
      });
    },
    refreshAuctionsForSearch(locale){
      this.auctions.searchLoading = true;
      DataService.refreshAuctionCache(locale).then(() => {
        if (!this.auctions.search || !this.auctions.search.trim()) {
          this.auctions.searchLoading = false;
          return;
        }
        DataService.getAuctionsForSearchQuery(locale, this.auctions.search).then(res => {
          this.auctions.searchResults = this.normalizeAuctionItems(res?.items || []);
          this.auctions.lastUpdated = res?.lastUpdated || null;
          this.$forceUpdate();
        }).finally(() => {
          this.auctions.searchLoading = false;
        });
      }).catch(() => {
        this.auctions.searchLoading = false;
      });
    },
    getAuctionsForSearch(locale){
      this.auctions.searchLoading = true;
      DataService.getAuctionsForSearchQuery(locale, this.auctions.search).then(res => {
        this.auctions.searchResults = this.normalizeAuctionItems(res?.items || []);
        this.auctions.lastUpdated = res?.lastUpdated || null;
      }).catch(() => {
        this.auctions.searchResults = [];
      }).finally(() => {
        this.auctions.searchLoading = false;
      });
    },
    hasExpandableDetails(item) {
      return item.reagents && item.reagents.length > 0;
    },
    // Gibt den Index des aktuell gewählten Tiers für ein Reagenz zurück
    getSelectedTierIndex(itemId, rIdx, reagent) {
      const key = `${itemId}_${rIdx}`;
      if (this.auctions.selectedTiers[key] !== undefined) return this.auctions.selectedTiers[key];
      // Default: günstigster Rang
      const cheapestIdx = reagent.tiers.findIndex(t => t.cheapest);
      return cheapestIdx >= 0 ? cheapestIdx : 0;
    },
    // Klick auf Rang-Zeile → als ausgewählt setzen
    selectTier(itemId, rIdx, tierIndex) {
      const key = `${itemId}_${rIdx}`;
      this.$set(this.auctions.selectedTiers, key, tierIndex);
    },
    // Berechnet die Gesamtkosten aller Reagenzien anhand der gewählten Tiers
    expansionAbbr(id) {
      const map = {
        1: 'VANILLA', 2: 'TBC', 3: 'WoTLK', 4: 'Cata',
        5: 'MoP', 6: 'WoD', 7: 'Legion', 8: 'BfA',
        9: 'SL',  10: 'DF', 11: 'TWW', 12: 'MID'
      };
      return map[id] ?? id;
    },
    expansionLabel(id) {
      const map = {
        1: 'Classic', 2: 'The Burning Crusade', 3: 'Wrath of the Lich King',
        4: 'Cataclysm', 5: 'Mists of Pandaria', 6: 'Warlords of Draenor',
        7: 'Legion', 8: 'Battle for Azeroth', 9: 'Shadowlands',
        10: 'Dragonflight', 11: 'The War Within', 12: 'Midnight'
      };
      return map[id] ?? id;
    },
    calcCraftCost(item) {
      if (!item.reagents || item.reagents.length === 0) return null;
      let total = 0;
      for (let rIdx = 0; rIdx < item.reagents.length; rIdx++) {
        const reagent = item.reagents[rIdx];
        const tierIdx = this.getSelectedTierIndex(item.itemId, rIdx, reagent);
        const tier = reagent.tiers[tierIdx];
        if (!tier || !tier.totalPrice || !tier.totalPrice.total) return null;
        total += tier.totalPrice.total;
      }
      if (total <= 0) return null;
      return {
        total,
        gold: Math.floor(total),
        silver: Math.round((total - Math.floor(total)) * 100).toString().padStart(2, '0')
      };
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleString(this.$i18n.locale === 'de' ? 'de-DE' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    },

    ilvlTrackClass(ilvl) {
      if (ilvl == null) return '';
      const tracks = this.currentSeasonDefItemLevel.tracks || {};
      const keys = Object.keys(tracks);
      // use the highest track that includes the ilvl
      for (let i = keys.length - 1; i >= 0; i--) {
        if (tracks[keys[i]].includes(ilvl)) {
          return `cell--track-${keys[i]}`;
        }
      }
      // ilvl is between two tracks – find next higher track
      for (let i = keys.length - 1; i >= 0; i--) {
        const arr = tracks[keys[i]];
        if (ilvl >= arr[0] && ilvl <= arr[arr.length - 1]) {
          return `cell--track-${keys[i]}`;
        }
      }
      return '';
    },

    trackCellClass(trackKey) {
      return `cell--track-${trackKey}`;
    },
    craftCellClass(rank) {
      return `cell--craft-${rank}`;
    },
    crestCellClass(rank) {
      return `cell--crest-${rank}`;
    },
    /** Returns the localized crest label via i18n: item_level.crest_details.{seasonId}.{key} */
    crestLabel(rank) {
      const cr = (this.currentSeasonDefItemLevel.crestRanks || []).find(c => c.rank === rank);
      if (!cr) return `Crest ${rank}`;
      const seasonId = this.currentSeasonDefItemLevel.id;
      return this.$t(`item_level.crest_details.${seasonId}.${cr.key}`);
    },
    /** Returns the localized crafting label via i18n: item_level.crafting_details.{seasonId}.{key} */
    craftLabel(rank) {
      const cf = (this.currentSeasonDefItemLevel.craftingRanks || []).find(c => c.rank === rank);
      if (!cf) return `Crafting ${rank}`;
      const seasonId = this.currentSeasonDefItemLevel.id;
      return this.$t(`item_level.crafting_details.${seasonId}.${cf.key}`);
    },

    /** Returns CSS classes array for a raid cell */
    raidCellClasses(raid) {
      if (!raid) return [];
      return [
        'cell--raid-' + raid.difficulty,
        'raid-block',
        'raid-block--' + raid.position
      ];
    },

    getAffixDetails(affix) {
      return this.affixMapping[affix];
    },
    getCurrentWeekNumber() {
      const week0 = new Date(this.EU_START_WEEK * 1000);

      const now = Math.floor(Date.now() / 1000);
      let startWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() - 6 * this.SECONDS_PER_DAY + 5 * 3600)) * 1000);
      let endWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() + 7 * this.SECONDS_PER_DAY - 1 + 4 * 3600 + 59 * 60 + 59)) * 1000);
      if (!(now >= Math.floor(startWeek.getTime() / 1000) && now <= Math.floor(endWeek.getTime() / 1000))) {
        startWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() + 7 * this.SECONDS_PER_DAY + 5 * 3600)) * 1000);
        endWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() + 14 * this.SECONDS_PER_DAY - 1 + 4 * 3600 + 59 * 60 + 59)) * 1000);
        if (!(now >= Math.floor(startWeek.getTime() / 1000) && now <= Math.floor(endWeek.getTime() / 1000))) {
          startWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() - 7 * this.SECONDS_PER_DAY + 5 * 3600)) * 1000);
          endWeek = new Date((now + (this.SECONDS_PER_DAY * 2 - this.SECONDS_PER_DAY * new Date(now * 1000).getDay() + this.SECONDS_PER_DAY - 1 + 4 * 3600 + 59 * 60 + 59)) * 1000);
        }
      }

      const thisWeek = new Date(startWeek);

      return Math.floor((thisWeek.getTime() - week0.getTime()) / (1000 * 60 * 60 * 24 * 7));
    },
    getCurrentAffixes(weekNumber, season) {
      let affixesTurn = this.affixesTurn[season];
      const index = (weekNumber + this.EU_DELAY) % affixesTurn.length;
      return affixesTurn[index];
    },
    normalizeAuctionItem(item) {
      const guildBankExisting = item.guildBankExisting ?? 0;
      const guildBankExpected = item.guildBankExpected ?? 0;
      const guildBankMissing = item.guildBankMissing ?? Math.max(0, guildBankExpected - guildBankExisting);
      const priceTotal = item.price?.total ?? 0;
      return {
        ...item,
        guildBankExisting,
        guildBankExpected,
        guildBankMissing,
        priceTotal,
        missingTotalPrice: guildBankMissing * priceTotal
      };
    },
    normalizeAuctionItems(items) {
      return (items || []).map(item => this.normalizeAuctionItem(item));
    }
  },

  computed: {
    raceClassRaces() {
      // Rassen aus dem ersten Klassen-Eintrag ableiten (Reihenfolge erhalten)
      const firstClass = Object.values(this.raceClassCombination)[0] || [];
      return firstClass.map(obj => Object.keys(obj)[0]);
    },
    currentUser() {
      return this.$store.getters["auth/user"];
    },
    manageGuildBank() {
      return this.currentUser && this.currentUser.rights?.includes("MANAGE_GUILD_BANK");
    },
    expectedHasUnsavedChanges() {
      const current = this.guildBankExpectedDialog.entries;
      const saved = this.guildBankExpectedDialog.savedSnapshot;
      if (current.length !== saved.length) return true;
      const savedMap = {};
      saved.forEach(e => { savedMap[e.itemId] = e.amount; });
      return current.some(e => savedMap[e.itemId] !== e.amount);
    },
    expectedDialogAvailableItems() {
      const usedIds = new Set(this.guildBankExpectedDialog.entries.map(e => String(e.itemId)));
      return this.auctions.currentResults
        .filter(i => !usedIds.has(String(i.itemId)))
        .map(i => ({
          itemId: String(i.itemId),
          label: i.label || String(i.itemId),
          icon: i.icon,
          craftingQualityTier: i.details?.craftingQualityTier ?? null
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    sortedExpectedDialogEntries() {
      return [...this.guildBankExpectedDialog.entries].sort((a, b) => {
        const labelA = (this.getItemForId(a.itemId)?.label ?? a.itemId).toLowerCase();
        const labelB = (this.getItemForId(b.itemId)?.label ?? b.itemId).toLowerCase();
        return labelA.localeCompare(labelB);
      });
    },

    filteredCurrentResults() {
      const maxQuality = this.auctions.currentFilter.onlyMaxQuality
          ? Math.max(...this.auctions.currentResults.map(i => i.details?.craftingQualityTier ?? 0))
          : null;
      return this.auctions.currentResults.filter(item => {
        const search = this.auctions.currentFilter.search?.toLowerCase() || '';
        if (search && !item.label?.toLowerCase().includes(search)) return false;
        if (this.auctions.currentFilter.category && item.category !== this.auctions.currentFilter.category) return false;
        if (this.auctions.currentFilter.priceCategory && item.priceCategory !== this.auctions.currentFilter.priceCategory) return false;
        if (this.auctions.currentFilter.expansion != null && item.details?.expansion !== this.auctions.currentFilter.expansion) return false;
        if (this.auctions.currentFilter.onlyWithExpected && !(item.guildBankExpected > 0)) return false;
        return !(maxQuality != null && maxQuality > 0 && (item.details?.craftingQualityTier ?? maxQuality) !== maxQuality);
      });
    },

    missingItemsTotalCost() {
      let total = 0;
      for (const item of this.filteredCurrentResults) {
        total += item.missingTotalPrice ?? 0;
      }
      return {
        total,
        gold: Math.floor(total).toLocaleString(this.$i18n.locale === 'de' ? 'de-DE' : 'en-US'),
        silver: Math.round((total - Math.floor(total)) * 100).toString().padStart(2, '0')
      };
    },
    existingItemsTotalCost() {
      let total = 0;
      for (const item of this.filteredCurrentResults) {
        const existing = item.guildBankExisting ?? 0;
        if (existing <= 0) continue;
        total += existing * (item.priceTotal ?? 0);
      }
      return {
        total,
        gold: Math.floor(total).toLocaleString(this.$i18n.locale === 'de' ? 'de-DE' : 'en-US'),
        silver: Math.round((total - Math.floor(total)) * 100).toString().padStart(2, '0')
      };
    },

    auctionCategoryOptions() {
      const categories = [...new Set(this.auctions.currentResults.map(i => i.category).filter(Boolean))];
      return categories.map(c => ({ text: this.$t('information.auction.category.' + c), value: c }));
    },

    darkMoonFairyHeaders() {
      return [
        { text: this.$t('information.darkmoon_faire.headers.profession'), value: 'profession' },
        { text: this.$t('information.darkmoon_faire.headers.quest'),      value: 'quest' },
        { text: this.$t('information.darkmoon_faire.headers.materials'),  value: 'materials' },
      ];
    },

    auctionPriceCategoryOptions() {
      const cats = [...new Set(this.auctions.currentResults.map(i => i.priceCategory).filter(Boolean))];
      return cats.map(c => ({ text: this.$t('information.auction.priceCategory.' + c), value: c }));
    },

    auctionExpansionOptions() {
      const ids = [...new Set(this.auctions.currentResults.map(i => i.details?.expansion).filter(v => v != null))].sort((a, b) => a - b);
      return ids.map(id => ({ text: this.expansionLabel(id), value: id }));
    },

    filteredSearchResults() {
      return this.auctions.searchResults.filter(item => {
        const search = this.auctions.searchFilter.search?.toLowerCase() || '';
        if (search && !item.label?.toLowerCase().includes(search)) return false;
        if (this.auctions.searchFilter.priceCategory && item.priceCategory !== this.auctions.searchFilter.priceCategory) return false;
        return !(this.auctions.searchFilter.expansion != null && item.details?.expansion !== this.auctions.searchFilter.expansion);

      });
    },

    auctionSearchPriceCategoryOptions() {
      const cats = [...new Set(this.auctions.searchResults.map(i => i.priceCategory).filter(Boolean))];
      return cats.map(c => ({ text: this.$t('information.auction.priceCategory.' + c), value: c }));
    },

    auctionSearchExpansionOptions() {
      const ids = [...new Set(this.auctions.searchResults.map(i => i.details?.expansion).filter(v => v != null))].sort((a, b) => a - b);
      return ids.map(id => ({ text: this.expansionLabel(id), value: id }));
    },

    auctionHeaders() {
      return [
        { text: this.$t('information.auction.headers.item'), value: 'label', align: 'start' },
        { text: this.$t('information.auction.headers.expansion'), value: 'details.expansion', align: 'start', sortable: true },
        { text: this.$t('information.auction.headers.priceCategory'), value: 'priceCategory', align: 'start' },
        { text: this.$t('information.auction.headers.price'), value: 'priceTotal', align: 'end', sortable: true },
        { text: this.$t('information.auction.headers.amount'), value: 'amount', align: 'start' }
      ];
    },

    auctionCurrentHeaders() {
      return [
        { text: this.$t('information.auction.headers.item'), value: 'label', align: 'start' },
        { text: this.$t('information.auction.headers.category'), value: 'category', align: 'start' },
        { text: this.$t('information.auction.headers.expansion'), value: 'details.expansion', align: 'start', sortable: true },
        { text: this.$t('information.auction.headers.priceCategory'), value: 'priceCategory', align: 'start' },
        { text: this.$t('information.auction.headers.price'), value: 'priceTotal', align: 'end', sortable: true },
        { text: this.$t('information.auction.headers.guild_bank_missing_total_price'), value: 'missingTotalPrice', align: 'end', sortable: true },
        { text: this.$t('information.auction.headers.amount'), value: 'amount', align: 'start' },
        { text: this.$t('information.auction.headers.guild_bank_existing'), value: 'guildBankExisting', align: 'start', sortable: true },
        { text: this.$t('information.auction.headers.guild_bank_expected'), value: 'guildBankExpected', align: 'start', sortable: true },
        { text: this.$t('information.auction.headers.guild_bank_missing'), value: 'guildBankMissing', align: 'start', sortable: true },
        { text: '', value: 'data-table-expand', align: 'end', sortable: false, width: '40px' },
      ];
    },

    canRefresh() {
      if (!this.auctions.lastUpdated) return true;
      return (new Date(Date.now()) - new Date(this.auctions.lastUpdated).getTime()) > 5 * 60 * 1000;
    },

    seasonsItemLevel(){
      return this.seasons.filter(x => x.showItemLevel);
    },

    seasonsMPlus(){
      return this.seasons.filter(x => x.showMPlus);
    },

    currentSeasonDefItemLevel() {
      return constants.ITEM_LEVEL_SEASONS[this.currentSeasonItemLevel] ?? Object.values(constants.ITEM_LEVEL_SEASONS)[0];
    },

    trackKeys() {
      return Object.keys(this.currentSeasonDefItemLevel.tracks || {});
    },

    craftingRanksList() {
      return this.currentSeasonDefItemLevel.craftingRanks || [];
    },

    hasRaidTable() {
      return (this.currentSeasonDefItemLevel.raidTable || []).length > 0;
    },

    // all unique ilvls from tracks + crests + crafting + raid (sorted ascending)
    allIlvls() {
      const s = this.currentSeasonDefItemLevel;
      const set = new Set();
      for (const arr of Object.values(s.tracks || {})) arr.forEach(v => set.add(v));
      for (const key of Object.keys(s.crestCostsByIlvl || {})) set.add(Number(key));
      for (const cr of (s.craftingRanks || [])) cr.range.forEach(v => set.add(v));
      for (const r of (s.raidTable || [])) r.range.forEach(v => set.add(v));
      return [...set].sort((a, b) => a - b);
    },

    combinedRows() {
      const s = this.currentSeasonDefItemLevel;
      const ilvls = this.allIlvls;

      // track rank for ilvl (Rang 1..6 or null) – based on index in track array
      const trackRankMap = {};
      for (const [key, arr] of Object.entries(s.tracks || {})) {
        trackRankMap[key] = {};
        arr.forEach((v, i) => { trackRankMap[key][v] = i + 1; });
      }

      // crafting rank for ilvl (Rang 1..5 or null) – based on index in range array
      const craftRankMap = {};
      for (const cr of (s.craftingRanks || [])) {
        craftRankMap[cr.rank] = {};
        cr.range.forEach((v, i) => { craftRankMap[cr.rank][v] = i + 1; });
      }

      // crest amount for ilvl and crest type (amount or null) – based on crestCostsByIlvl
      const crestMap = {};
      for (const [ilvlStr, entries] of Object.entries(s.crestCostsByIlvl || {})) {
        const map = {};
        for (const e of entries) {
          map[e.crest] = (map[e.crest] || 0) + e.amount;
        }
        crestMap[Number(ilvlStr)] = map;
      }

      return ilvls.map(ilvl => {
        // track cells – object with rank + position flags for fake-rowspan rendering
        const tracks = {};
        for (const t of this.trackKeys) {
          const rank = trackRankMap[t]?.[ilvl];
          if (rank != null) {
            const arr = s.tracks[t];
            const len = arr.length;
            const isSingle = len === 1;
            const isFirst = !isSingle && rank === 1;
            const isLast  = !isSingle && rank === len;
            tracks[t] = { rank, isFirst, isLast, isSingle };
          } else {
            tracks[t] = null;
          }
        }

        // crafting cells – 2 columns (odd ranks col 0, even ranks col 1)
        const craftingCols = [null, null];
        for (const cf of this.craftingRanksList) {
          const rank = craftRankMap[cf.rank]?.[ilvl];
          if (rank != null) {
            const colIdx = (cf.rank - 1) % 2; // 0-based: rank 1→col0, rank 2→col1, rank 3→col0, ...
            let crestLabel = null;
            let crestRank = null;
            if (cf.crest_info) {
              crestRank = cf.crest_info.crest;
              const crName = this.crestLabel(cf.crest_info.crest);
              crestLabel = cf.crest_info.amount ? `${crName} × ${cf.crest_info.amount}` : crName;
            }
            craftingCols[colIdx] = {
              rank: cf.rank,
              label: `${this.craftLabel(cf.rank)} - Q${rank}`,
              crestLabel,
              crestRank
            };
          }
        }

        // crafting cells (legacy – kept for compatibility)
        const crafting = {};
        for (const cf of this.craftingRanksList) {
          const rank = craftRankMap[cf.rank]?.[ilvl];
          crafting[cf.rank] = rank != null
              ? `Rang ${rank}`
              : null;
        }

        // crest cells – 2 columns (odd crest ranks col 0, even ranks col 1), format: "10× Held"
        const crestCols = [null, null];
        const crestEntries = s.crestCostsByIlvl?.[ilvl] || [];
        for (const e of crestEntries) {
          const colIdx = (e.crest - 1) % 2; // rank 1→col0, rank 2→col1, rank 3→col0, ...
          const crName = this.crestLabel(e.crest);
          crestCols[colIdx] = {
            crestRank: e.crest,
            label: `${e.amount}× ${crName}`
          };
        }

        // crest cells (legacy)
        const crests = crestMap[ilvl] || {};

        // raid cells – determine highest difficulty for this ilvl and metadata for block rendering
        let raid = null;
        for (const r of (s.raidTable || [])) {
          const idx = r.range.indexOf(ilvl);
          if (idx !== -1) {
            const len = r.range.length;
            let position = 'middle';
            if (len === 1) position = 'single';
            else if (idx === 0) position = 'first';
            else if (idx === len - 1) position = 'last';

            raid = {
              difficulty: r.difficulty,
              position,
              showLabel: position === 'first' || position === 'single'
            };
          }
        }

        return { ilvl, tracks, crafting, craftingCols, crestCols, crests, raid };
      });
    },
  }
}
</script>

<style scoped>
/* ===== Hover-Effekt anpassen: Vuetify-Hover überschreiben ===== */
.itemlevel-table ::v-deep .v-data-table__wrapper table tbody tr:hover td,
.combined-table ::v-deep .v-data-table__wrapper table tbody tr:hover td,
.itemlevel-table ::v-deep tbody tr:hover td,
.combined-table ::v-deep tbody tr:hover td {
  background: inherit !important;
}

/* ===== Basis for tables ===== */
.itemlevel-table,
.combined-table {
  border-collapse: collapse;
  font-size: 13px;
  width: auto !important;
  table-layout: auto;
}

.itemlevel-table th,
.itemlevel-table td,
.combined-table th,
.combined-table td {
  border: 1px solid #444;
  padding: 3px 6px;
  vertical-align: middle;
  font-weight: 700;
  text-align: center !important;
}
.table-header-row th {
  background: rgba(255, 255, 255, 0.04);
}

.crest-inline {
  display: block;
  white-space: nowrap;
}

/* Bei mehreren Wappen: Zelle wird Flex-Container, jede Hälfte eigene Farbe */
.crest-multi {
  padding: 0 !important;
  display: flex !important;
  flex-direction: row;
  align-items: stretch;
  background: transparent !important;
}

.crest-half {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  white-space: nowrap;
  font-weight: 700;
  min-height: 100%;
}

/* Crest-Zellen in Dungeon/Delve linksbündig (nur bei Single-Crest) */
.itemlevel-table td.crest-cell-left:not(.crest-multi) {
  text-align: left !important;
  white-space: nowrap;
}

/* ===== Details table (combinated) ===== */
.combined-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.combined-table {
  background: #1e1e1e;
}

/* Feste Zeilenhöhe für alle Datenzeilen in der Detail-Tabelle */
.combined-table tbody tr {
  height: 32px;
}
.combined-table tbody tr td {
  height: 32px;
  max-height: 32px;
  overflow: hidden;
}

/* Nummerische Spalten in Dungeon/Delve-Tabellen */
.col-num {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  text-align: center !important;
  white-space: nowrap;
}

/* ilvl-Spalte in der Detail-Tabelle */
.col-ilvl {
  font-weight: 800;
  text-align: center !important;
  width: 1%;
  min-width: 55px;
  max-width: 55px;
  background: rgba(255, 255, 255, 0.04);
  white-space: nowrap;
}

.col-empty {
  background: rgba(255, 255, 255, 0.04);
}

.col-raid-empty {
  background: transparent;
}

/* Track-Name-Spalte (linke der 2 Track-Spalten) */
.col-track-name {
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  border-right: none !important;
}

/* Track-Rang-Spalte (rechte der 2 Track-Spalten) */
.col-track-rank {
  width: 30px !important;
  min-width: 30px;
  max-width: 30px;
  border-left: 1px dashed #555 !important;
  padding: 2px 4px !important;
}

.col-track-rank-empty {
  border-left: none !important;
}

/* Track-Name-Zelle: Border-Handling für fake-rowspan */
.track-name-cell {
  border-bottom: none !important;
  border-top: none !important;
  max-width: 120px !important;
  width: 120px !important;
}
.track-name-first {
  border-top: 1px solid #444 !important;
  border-bottom: none !important;
}
.track-name-last {
  border-top: none !important;
  border-bottom: 1px solid #444 !important;
}
.track-name-single {
  border-top: 1px solid #444 !important;
  border-bottom: 1px solid #444 !important;
}

.track-name-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Gruppenheader (oberste Zeile) – !important nötig wegen Vuetify */
.group-header {
  text-align: center !important;
  font-weight: 800;
  font-size: 13px;
  padding: 5px 6px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 2px solid #555;
  white-space: nowrap;
}

/* Spalten-Unterheader – gleichbreit für alle Spalten zwischen ilvl und Raid */
.col-sub {
  text-align: center !important;
  font-weight: 700;
  padding: 3px 3px;
  white-space: nowrap;
}

/* Header-Label: darf umbrechen (mehrzeilig), damit Spalten schmaler werden */
.header-label {
  display: block;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  min-width: 36px;
  font-size: 11px;
}

/* Daten-Zellen – kompakt */
.cell-data {
  white-space: nowrap;
  width: auto;
  padding: 2px 4px;
  height: 32px;
  vertical-align: middle;
}

.cell-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Handwerkswappen-Info: größer, heller, weißes Outline */
.craft-crest-info {
  font-size: 12px;
  font-weight: 700;
  margin-top: -5px;
  filter: brightness(1.2);
}

/* Raid-Spalte */
.cell-data-raid {
  white-space: nowrap;
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  padding: 2px 4px;
  height: 32px;
  vertical-align: middle;
}

/* Crafting-Spalten */
.cell-data-craft {
  white-space: nowrap;
  width: 140px;
  min-width: 140px;
  max-width: 140px;
  padding: 2px 4px;
  height: 32px;
  vertical-align: middle;
}

/* ===== Upgrade Track Farben ===== */
.header--track-adventurer { color: #62d26f; }
.header--track-veteran    { color: #5bb6ff; }
.header--track-champion   { color: #c07bff; }
.header--track-hero       { color: #ffd36a; }
.header--track-myth       { color: #ff6a6a; }

.cell--track-adventurer { color: #62d26f; background: rgba(98, 210, 111, 0.10); }
.cell--track-veteran    { color: #5bb6ff; background: rgba(91, 182, 255, 0.10); }
.cell--track-champion   { color: #c07bff; background: rgba(192, 123, 255, 0.10); }
.cell--track-hero       { color: #ffd36a; background: rgba(255, 211, 106, 0.10); }
.cell--track-myth       { color: #ff6a6a; background: rgba(255, 106, 106, 0.10); }

/* ===== Crest Farben (Türkis – Rank-basiert, 5 deutliche Abstufungen) ===== */
.header--crest-1 { color: #b0f8e8; }
.header--crest-2 { color: #60e0c8; }
.header--crest-3 { color: #30c8a8; }
.header--crest-4 { color: #10a080; }
.header--crest-5 { color: #007858; }

.cell--crest-1 { color: #b0f8e8; background: rgba(176, 248, 232, 0.20); }
.cell--crest-2 { color: #60e0c8; background: rgba(96, 224, 200, 0.18); }
.cell--crest-3 { color: #30c8a8; background: rgba(48, 200, 168, 0.16); }
.cell--crest-4 { color: #10a080; background: rgba(16, 160, 128, 0.18); }
.cell--crest-5 { color: #007858; background: rgba(0, 120, 88, 0.22); }

/* Textfarbe für Crests bei gesplittetem Hintergrund */
.text--crest-1 { color: #b0f8e8; }
.text--crest-2 { color: #60e0c8; }
.text--crest-3 { color: #30c8a8; }
.text--crest-4 { color: #10a080; }
.text--crest-5 { color: #007858; }

/* ===== Crafting Farben (Orange/Amber – Rank-basiert, 5 deutliche Abstufungen) ===== */

/* ===== Raid Farben – einheitlich grauer Hintergrund und Text für alle Schwierigkeiten ===== */
.cell--raid-lfr     { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }
.cell--raid-normal  { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }
.cell--raid-heroic  { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }
.cell--raid-mythic  { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }
.cell--raid-mythic-low  { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }
.cell--raid-mythic-high  { color: #b0b0b0; background: rgba(176, 176, 176, 0.10); }

/* Raid-Textfarben */
.text--raid-lfr     { color: #b0b0b0; }
.text--raid-normal  { color: #b0b0b0; }
.text--raid-heroic  { color: #b0b0b0; }
.text--raid-mythic  { color: #b0b0b0; }

/* Raid block styling to visually merge rows */
.combined-table td.raid-block {
  border-left: 1px solid #444;
  border-right: 1px solid #444;
}
.combined-table td.raid-block--first {
  border-top: 1px solid #444;
  border-bottom: none !important;
}
.combined-table td.raid-block--middle {
  border-top: none !important;
  border-bottom: none !important;
}
.combined-table td.raid-block--last {
  border-top: none !important;
  border-bottom: 1px solid #444;
}
.combined-table td.raid-block--single {
  border-top: 1px solid #444;
  border-bottom: 1px solid #444;
}

.raid-label {
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.header--craft-1 { color: #f0c880; }
.header--craft-2 { color: #e8a040; }
.header--craft-3 { color: #d07820; }
.header--craft-4 { color: #b05808; }
.header--craft-5 { color: #883800; }

.cell--craft-1 { color: #f0c880; background: rgba(240, 200, 128, 0.15); }
.cell--craft-2 { color: #e8a040; background: rgba(232, 160, 64, 0.15); }
.cell--craft-3 { color: #d07820; background: rgba(208, 120, 32, 0.16); }
.cell--craft-4 { color: #b05808; background: rgba(176, 88, 8, 0.18); }
.cell--craft-5 { color: #883800; background: rgba(136, 56, 0, 0.22); }

/* ===== Auction Detail Panel ===== */
.auction-detail-panel {
  background: rgba(255, 255, 255, 0.04);
  border-top: 1px solid #444;
  padding: 10px 16px;
}

.auction-detail-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 24px;
}

.auction-detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
}

.auction-detail-label {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  text-transform: uppercase;
  font-size: 15px;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.auction-detail-value {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 18px;
}

.auction-reagents-title {
  margin-top: 4px;
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.4);
}

.auction-reagents-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.auction-reagent-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.auction-reagent-name {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 180px;
}

.auction-reagent-qty {
  color: rgba(255, 255, 255, 0.5);
  min-width: 30px;
  font-size: 11px;
}

/* ===== Reagenz-Block (neue Rang-Darstellung) ===== */
.auction-reagent-block {
  margin-bottom: 10px;
}

.auction-reagent-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.auction-reagent-qty-badge {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}

.auction-reagent-mat-name {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.auction-reagent-tier-table {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  overflow: hidden;
}

.auction-reagent-tier-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  padding: 4px 8px;
}

.auction-reagent-tier-row:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.auction-reagent-tier-header {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.03);
  padding: 3px 8px;
}

.art-row--selected {
  background: rgba(76, 175, 80, 0.18);
  outline: 1px solid rgba(76, 175, 80, 0.35);
}

.art-col-tier  { width: 20px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.art-col-name  { flex: 1; display: flex; align-items: center; gap: 5px; min-width: 0; overflow: hidden; }
.art-col-price { min-width: 100px; display: flex; align-items: center; gap: 3px; color: rgba(255,255,255,0.85); font-weight: 600; }
.art-col-total { min-width: 100px; display: flex; align-items: center; gap: 3px; color: rgba(255,255,255,0.6); }

/* ===== Craft-Kosten unterhalb der Reagenzien, rechtsbündig ===== */
.auction-craftcost-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.auction-craftcost-box {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 6px 14px;
}

.auction-craftcost-label {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}

.auction-craftcost-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

/* ===== Expansion Badge ===== */
.expansion-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.expansion-badge--empty {
  opacity: 0.3;
  border-color: transparent;
  background: transparent;
}

/* ===== Auction Table: alles linksbündig, Sort-Pfeil rechts ===== */
.auction-table.v-data-table td,
.auction-table.v-data-table th {
  text-align: left !important;
}
/* Zellen-Inhalt linksbündig */
.auction-table.v-data-table td {
  justify-content: flex-start !important;
}
/* Header-Inhalt: Text links, Sort-Icon rechts */
.auction-table .v-data-table-header th {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-start !important;
}
.auction-table .v-data-table-header th > span {
  order: 0;
}
.auction-table .v-data-table-header th .v-icon {
  order: 1 !important;
  margin-left: 4px !important;
  margin-right: 0 !important;
}
/* expand-Spalte ganz rechts bleibt rechtsbündig */
.auction-table .v-data-table-header th:last-child {
  justify-content: flex-end !important;
}

/* ===== Responsive ===== */
@media (max-width: 1264px) {
  .itemlevel-table,
  .combined-table {
    font-size: 12px;
  }
  .itemlevel-table th,
  .itemlevel-table td,
  .combined-table th,
  .combined-table td {
    padding: 4px 5px;
  }
  .group-header {
    font-size: 12px;
  }
  .col-sub, .cell-data {
    padding: 3px 2px;
    width: 90px;
    min-width: 90px;
    max-width: 90px;
  }
  .cell-data-craft {
    padding: 3px 2px;
    width: 140px;
    min-width: 140px;
    max-width: 140px;
    height: 38px;
  }
  .cell-data-raid {
    width: 95px;
    min-width: 95px;
    max-width: 95px;
    height: 38px;
  }
  .cell-data {
    height: 38px;
  }
  .combined-table tbody tr {
    height: 38px;
  }
  .combined-table tbody tr td {
    height: 38px;
    max-height: 38px;
  }
  .col-ilvl {
    width: 1% !important;
    min-width: 64px !important;
    max-width: 64px !important;
  }
  .header-label {
    min-width: 42px;
    font-size: 11px;
  }
}

@media (max-width: 960px) {
  .itemlevel-table,
  .combined-table {
    font-size: 11px;
  }
  .itemlevel-table th,
  .itemlevel-table td,
  .combined-table th,
  .combined-table td {
    padding: 3px 4px;
  }
  .group-header {
    font-size: 11px;
    padding: 5px 4px;
  }
  .col-sub, .cell-data {
    padding: 2px 2px;
    width: 75px;
    min-width: 75px;
    max-width: 75px;
  }
  .cell-data-craft {
    padding: 2px 2px;
    width: 120px;
    min-width: 120px;
    max-width: 120px;
    height: 36px;
  }
  .cell-data-raid {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    height: 36px;
  }
  .cell-data {
    height: 36px;
  }
  .combined-table tbody tr {
    height: 36px;
  }
  .combined-table tbody tr td {
    height: 36px;
    max-height: 36px;
  }
  .col-ilvl {
    width: 1% !important;
    min-width: 58px !important;
    max-width: 58px !important;
  }
  .header-label {
    min-width: 36px;
    font-size: 10px;
  }
}

/* ===== Race / Class Tabelle ===== */
.rc-table-wrap {
  display: inline-block;
  max-width: 100%;
  overflow-x: auto;
}
.race-class-table {
  width: auto !important;
}
.race-class-table th,
.race-class-table td {
  padding: 2px 6px !important;
  white-space: nowrap;
}
.rc-col-class {
  min-width: 110px;
}
.rc-col-race {
  font-size: 11px;
  min-width: 54px;
}
.rc-cell {
  height: 28px;
}
</style>

